import { language, onLanguageChange, phrases } from "../i18n";
import type { KeptStore } from "./identity";
import { cleanName, keptName, NAME_KEY, nameInLanguage, rerolledName } from "./name";
import { type ClaimOutcome, freeNameFrom, heldNameFrom, type NameDesk } from "./nameClaim";

/**
 * The front page's name field (B3.1; L10N2: a generated name in the page's language; NM2: a name
 * owned by an account). The field shows the kept name, the dice roll another, and a typed one is
 * cleaned. With names on (`NameRules`), a signed-in visitor's name is claimed for the account and
 * a guest's is checked free: a name another account holds is never left in the field, and the
 * line under it says why. A generated name follows the language unless the account holds it.
 */

/** NM2: the names accounts own, where the page's switch and the Worker keep them. */
export interface NameRules {
  readonly desk: NameDesk;
  /** Whether the visitor is signed in with a session: claims go through it. */
  readonly signedIn: boolean;
  /** The name the account holds, as the Worker last said; undefined when none. */
  readonly held: string | undefined;
  /** Keeps the name the account now holds (`HELD_NAME_KEY`). */
  readonly keep: (held: string) => void;
  /** The Worker no longer takes the session: the page signs the visitor out. */
  readonly expired: () => void;
}

export interface NameField {
  /** The visitor's name as it stands. */
  name(): string;
  close(): void;
}

/**
 * Where a wanted name came from: typed (refused, the field goes back), rolled by the dice (rolls
 * on, quietly) or kept (the page's own: rolls on, and says the old one was taken).
 */
type Origin = "typed" | "rolled" | "kept";

/** What the line under the field says, said again in a new language. */
interface Said {
  readonly state: "yours" | "refused";
  readonly words: () => string;
}

const YOURS: Said = { state: "yours", words: () => phrases().front.names.yours };

/** The line for a claim that did not hold `wanted`; undefined for one that leaves it unsaid. */
function refusalOf(outcome: ClaimOutcome, wanted: string): Said | undefined {
  switch (outcome.result) {
    case "taken":
      return { state: "refused", words: () => phrases().front.names.taken(wanted) };
    case "reserved":
      return { state: "refused", words: () => phrases().front.names.reserved };
    case "session":
      return { state: "refused", words: () => phrases().front.names.signInAgain };
    case "unreachable":
      return { state: "refused", words: () => phrases().front.names.unsaved };
    default:
      return undefined;
  }
}

export function mountNameField(options: {
  readonly input: HTMLInputElement;
  readonly roll: HTMLButtonElement;
  /** The line under the field; none, and nothing is said. */
  readonly note: HTMLElement | null;
  readonly data: KeptStore;
  readonly draw: () => number;
  readonly names?: NameRules;
}): NameField {
  const { input, data, names } = options;
  let held = names?.signedIn ? names.held : undefined;
  let name = held ?? keptName(data, options.draw, language());
  let said: Said | undefined;
  /** Only the newest question's answer lands: a typed name after a roll wins over the roll. */
  let asked = 0;

  const showNote = (): void => {
    const note = options.note;
    if (!note) {
      return;
    }
    note.hidden = said === undefined;
    note.textContent = said?.words() ?? "";
    if (said) {
      note.dataset.state = said.state;
    } else {
      delete note.dataset.state;
    }
  };
  const say = (next: Said | undefined): void => {
    said = next;
    showNote();
  };
  const accept = (next: string): void => {
    name = next;
    input.value = next;
    data.set(NAME_KEY, next);
  };
  accept(name);
  const rolled = (): string => rerolledName(name, options.draw, language());

  /** `wanted` becomes the name: claimed for the account, or checked free for a guest. */
  const settle = async (wanted: string, origin: Origin): Promise<void> => {
    const previous = name;
    accept(wanted);
    if (!names) {
      return;
    }
    asked += 1;
    const mine = asked;
    if (names.signedIn) {
      const outcome =
        origin === "typed"
          ? await names.desk.claim(wanted)
          : await heldNameFrom(wanted, rolled, names.desk);
      if (mine !== asked) {
        return;
      }
      if (outcome.result === "held") {
        accept(outcome.name);
        held = outcome.name;
        names.keep(outcome.name);
        say(YOURS);
        return;
      }
      if (outcome.result !== "off") {
        accept(previous);
      }
      if (outcome.result === "session") {
        names.expired();
      }
      say(refusalOf(outcome, wanted));
      return;
    }
    const found = await freeNameFrom(wanted, rolled, names.desk);
    if (mine !== asked) {
      return;
    }
    if (found === wanted) {
      say(undefined);
    } else if (origin === "typed") {
      accept(previous);
      say({ state: "refused", words: () => phrases().front.names.taken(wanted) });
    } else {
      accept(found);
      say(
        origin === "kept"
          ? { state: "refused", words: () => phrases().front.names.replaced(wanted) }
          : undefined,
      );
    }
  };

  input.addEventListener("change", () => {
    const typed = cleanName(input.value) ?? name;
    if (typed === name) {
      input.value = name;
      return;
    }
    void settle(typed, "typed");
  });
  options.roll.addEventListener("click", () => {
    void settle(rolled(), "rolled");
  });
  const unheard = onLanguageChange((next) => {
    showNote();
    if (held !== undefined && name === held) {
      return;
    }
    const spoken = nameInLanguage(name, next);
    if (spoken !== name) {
      void settle(spoken, "kept");
    }
  });

  if (held !== undefined) {
    say(YOURS);
  } else if (names) {
    void settle(name, "kept");
  }

  return {
    name: () => input.value,
    close: unheard,
  };
}
